import React, { useRef, useLayoutEffect, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Phone, MapPin, Clock, Send, User, Smartphone, Dog, Stethoscope, ShieldCheck } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const Contact = () => {
  const containerRef = useRef(null);
  const [formData, setFormData] = useState({ guardianName: '', petName: '', phone: '', concern: '' });

  const handleWhatsAppSubmit = (e) => {
    e.preventDefault();
    const { guardianName, petName, phone, concern } = formData;
    const message = `*New Appointment Request* 🐾\n\n*Guardian Name:* ${guardianName}\n*Pet Name:* ${petName || 'Not specified'}\n*Phone:* ${phone}\n*Clinical Concern:* ${concern}`;
    const whatsappUrl = `https://wa.me/919848954921?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.contact-header', 
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: "power3.out", scrollTrigger: { trigger: '.contact-header', start: "top 90%" } }
      );

      gsap.fromTo('.contact-item', 
        { x: -30, opacity: 0 },
        { x: 0, opacity: 1, stagger: 0.1, duration: 0.8, ease: "power3.out", scrollTrigger: { trigger: '.contact-info-grid', start: "top 85%" } }
      );

      gsap.fromTo('.contact-form-card', 
        { scale: 0.95, opacity: 0, y: 40 },
        { scale: 1, opacity: 1, y: 0, duration: 1.2, ease: "power4.out", scrollTrigger: { trigger: '.contact-form-card', start: "top 80%" } }
      );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  const contactDetails = [
    { 
      icon: <Phone size={24} />, 
      title: "Clinic Hotline", 
      val: "+91 9848954921", 
      sub: "24/7 Emergency Support",
      accent: "text-emerald-500",
      bg: "bg-emerald-50"
    },
    { 
      icon: <MapPin size={24} />, 
      title: "Location", 
      val: "Vanasthalipuram (Main)", 
      sub: "Hyderabad, Telangana",
      accent: "text-blue-500",
      bg: "bg-blue-50"
    },
    { 
      icon: <Clock size={24} />, 
      title: "Operating Hours", 
      val: "09:00 AM — 09:00 PM", 
      sub: "Open Daily • Holidays Included",
      accent: "text-amber-500",
      bg: "bg-amber-50"
    },
    { 
      icon: <Send size={24} />, 
      title: "Email Support", 
      val: "dr.sureshhvet97@gmail.com", 
      sub: "Clinical & Surgical Inquiries",
      accent: "text-rose-500",
      bg: "bg-rose-50"
    }
  ];

  return (
    <section ref={containerRef} className="py-24 md:py-32 bg-[#fbfcfd] relative overflow-hidden" id="contact">
      
      {/* Background elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/3 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-500/3 rounded-full blur-[100px] pointer-events-none" />

      <div className="section-container relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-start">
          
          {/* Left Column: Info */}
          <div className="space-y-12">
            <div className="contact-header space-y-6">
              <div className="flex items-center gap-3">
                <span className="w-10 h-px bg-primary/40"></span>
                <span className="text-primary font-black tracking-[0.25em] uppercase text-[10px]">Reach Out</span>
              </div>
              <h2 className="text-navy font-bold leading-[0.95] tracking-tight text-[clamp(2.5rem,7vw,4.5rem)]">
                Contact Dr. Suresh <br /> <em className="text-primary font-normal not-italic underline decoration-primary/20 underline-offset-8">Pet Care.</em>
              </h2>
              <p className="text-muted-foreground text-lg max-w-md leading-relaxed">
                Get in touch for expert veterinary consultation, emergency support, and specialized surgery for your pets.
              </p>
            </div>

            <div className="contact-info-grid grid gap-4">
              {contactDetails.map((item, i) => (
                <div key={i} className="contact-item group p-6 rounded-[2rem] bg-white border border-navy/5 hover:border-primary/20 hover:shadow-xl hover:shadow-navy/5 transition-all duration-500 flex items-center gap-6">
                  <div className={`w-14 h-14 rounded-2xl ${item.bg} ${item.accent} flex items-center justify-center transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110 shadow-sm`}>
                    {item.icon}
                  </div>
                  <div>
                    <h4 className="text-[11px] font-black uppercase tracking-[0.15em] text-navy/30 mb-1">{item.title}</h4>
                    <p className="text-lg font-bold text-navy group-hover:text-primary transition-colors">{item.val}</p>
                    <p className="text-[10px] font-bold text-primary/60 uppercase tracking-widest mt-1">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="contact-item inline-flex flex-col gap-3 p-8 rounded-[2.5rem] bg-navy text-white/90 shadow-2xl shadow-navy/20">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary">
                  <ShieldCheck size={20} />
                </div>
                <p className="text-xs font-bold tracking-wide">
                  Safe & Professional <br /> <span className="text-white/40 font-normal">Encrypted WhatsApp Data</span>
                </p>
              </div>
              <div className="h-px bg-white/10 w-full my-2" />
              <p className="text-[10px] text-white/50 leading-relaxed font-bold uppercase tracking-widest">
                Searching for a <strong>pet clinic near Vanasthalipuram</strong> or LB Nagar? Visit Dr. Suresh Pet Care Hospital for expert veterinary services.
              </p>
            </div>
          </div>

          {/* Right Column: Form */}
          <div className="contact-form-card relative">
            {/* Form Decorative Background */}
            <div className="absolute inset-0 bg-primary/5 rounded-[3rem] blur-2xl -z-10 translate-x-4 translate-y-4" />
            
            <div className="bg-white rounded-[3rem] border border-navy/5 shadow-2xl p-8 md:p-12 relative overflow-hidden">
              {/* Form header */}
              <div className="mb-10 flex items-center justify-between border-b border-navy/5 pb-8">
                <div>
                  <h3 className="text-2xl font-bold text-navy mb-2">Priority Booking</h3>
                  <p className="text-muted-foreground text-sm font-medium">Direct Clinical Path via WhatsApp</p>
                </div>
                <div className="hidden sm:block">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <Stethoscope size={24} />
                  </div>
                </div>
              </div>

              <form onSubmit={handleWhatsAppSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  {/* Guardian Name */}
                  <div className="space-y-2 group">
                    <label className="text-[10px] font-black uppercase tracking-widest text-navy/40 ml-4 group-focus-within:text-primary transition-colors">Guardian Name</label>
                    <div className="relative">
                      <User size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-navy/20 group-focus-within:text-primary transition-colors" />
                      <input 
                        required
                        type="text"
                        placeholder="e.g. Sarah Johnson"
                        className="w-full bg-slate-50 border border-navy/5 rounded-full py-4 pl-12 pr-6 outline-none text-sm font-bold text-navy focus:bg-white focus:border-primary/50 transition-all shadow-inner"
                        value={formData.guardianName}
                        onChange={(e) => setFormData({...formData, guardianName: e.target.value})}
                      />
                    </div>
                  </div>
                  {/* Phone Number */}
                  <div className="space-y-2 group">
                    <label className="text-[10px] font-black uppercase tracking-widest text-navy/40 ml-4 group-focus-within:text-primary transition-colors">Phone Number</label>
                    <div className="relative">
                      <Smartphone size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-navy/20 group-focus-within:text-primary transition-colors" />
                      <input 
                        required
                        type="tel"
                        placeholder="+91 XXXX XXX XXX"
                        className="w-full bg-slate-50 border border-navy/5 rounded-full py-4 pl-12 pr-6 outline-none text-sm font-bold text-navy focus:bg-white focus:border-primary/50 transition-all shadow-inner"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                {/* Pet Name */}
                <div className="space-y-2 group">
                  <label className="text-[10px] font-black uppercase tracking-widest text-navy/40 ml-4 group-focus-within:text-primary transition-colors">Pet Identification (Optional)</label>
                  <div className="relative">
                    <Dog size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-navy/20 group-focus-within:text-primary transition-colors" />
                    <input 
                      type="text"
                      placeholder="e.g. Max (Golden Retriever)"
                      className="w-full bg-slate-50 border border-navy/5 rounded-full py-4 pl-12 pr-6 outline-none text-sm font-bold text-navy focus:bg-white focus:border-primary/50 transition-all shadow-inner"
                      value={formData.petName}
                      onChange={(e) => setFormData({...formData, petName: e.target.value})}
                    />
                  </div>
                </div>

                {/* Concern */}
                <div className="space-y-2 group">
                  <label className="text-[10px] font-black uppercase tracking-widest text-navy/40 ml-4 group-focus-within:text-primary transition-colors">Clinical Concern</label>
                  <div className="relative">
                    <Send size={16} className="absolute left-6 top-6 text-navy/20 group-focus-within:text-primary transition-colors" />
                    <textarea 
                      required
                      placeholder="Briefly describe the medical status or surgical requirement..."
                      className="w-full bg-slate-50 border border-navy/5 rounded-[2rem] py-5 pl-14 pr-6 h-36 outline-none text-sm font-bold text-navy focus:bg-white focus:border-primary/50 transition-all shadow-inner resize-none leading-relaxed"
                      value={formData.concern}
                      onChange={(e) => setFormData({...formData, concern: e.target.value})}
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button type="submit" className="group/btn relative w-full h-16 bg-gradient-to-r from-[#25D366] to-[#1db958] rounded-full flex items-center justify-center gap-3 overflow-hidden shadow-xl shadow-green-500/20 hover:shadow-green-500/40 hover:-translate-y-1.5 transition-all duration-500">
                  <div className="absolute inset-0 bg-white/20 -translate-x-full group-hover/btn:translate-x-0 transition-transform duration-700" />
                  <span className="relative z-10 text-white font-bold uppercase tracking-widest text-xs">Confirm via WhatsApp</span>
                  <Send size={16} className="relative z-10 text-white group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-transform" />
                </button>

                <p className="text-center text-[10px] font-bold text-navy/30 uppercase tracking-widest">
                  Secure Link • Immediate clinical notification
                </p>
              </form>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Contact;